import {useState} from "react";
import {MenuItem, Select, FormControl, InputLabel, Checkbox, ListItemText} from "@mui/material";
import {translate} from "../../translate/translate";


export interface MonthYearOption {
    label: string;
    value: string,
    parent?: string,
    is_year?: boolean
}

interface MultilevelSelectYearComponentProps {
    options?: MonthYearOption[],
    changeValues: (values: string[]) => void,
    values?: string[],
}

const MultiLevelSelect: React.FC<MultilevelSelectYearComponentProps> = (
    {
        options = [],
        changeValues,
        values = []
    }
) => {
    const [selectedValues, setSelectedValues] = useState<string[]>(values);

    const selectMonth = (month: string, checked: boolean) => {
        setSelectedValues((prev) => {
            const parentYear = options.find((o) => o.value === month)?.parent;
            const allYearMonths = options.filter((o) => o.parent === parentYear).map((o) => o.value);

            let updatedValues = checked ? [...prev, month] : prev.filter((v) => v !== month);

            const selectedMonths = updatedValues.filter((v) => allYearMonths.includes(v));

            if (selectedMonths.length === allYearMonths.length) {
                updatedValues = [...updatedValues, parentYear!];
            } else {
                updatedValues = updatedValues.filter((v) => v !== parentYear);
            }

            changeValues(updatedValues);

            return updatedValues;
        });
    };

    const selectAllMonths = (year: string, checked: boolean) => {
        setSelectedValues((prev) => {
            const yearMonths = options.filter((o) => o.parent === year).map((o) => o.value);

            let updatedValues;

            if (checked) {
                updatedValues = Array.from(new Set([...prev, year, ...yearMonths]));
            } else {
                updatedValues = prev.filter((v) => v !== year && !yearMonths.includes(v));
            }

            changeValues(updatedValues);

            return updatedValues;
        });
    };

    const handleChange = (event: any) => {
        const value = event.target.value as string[];

        const lastSelected = value[value.length - 1];
        const isYear = options.find((o) => o.value === lastSelected)?.is_year;
        const missingValue = selectedValues.find(v => !value.includes(v));

        if (!missingValue) {
            if (isYear) {
                selectAllMonths(lastSelected, value.includes(lastSelected));
            } else {
                selectMonth(lastSelected, value.includes(lastSelected));
            }
        } else {
            const isMissingYear = options.find((o) => o.value === missingValue)?.is_year;
            if (isMissingYear) {
                selectAllMonths(missingValue, false);
            } else {
                selectMonth(missingValue, false);
            }
        }
    };

    const renderValues = (selected: string[]) => {
        if (allSelected) {
            return translate.t("all");
        }
        const s = selected.map((value) => {
            const isYear = options.find((o) => o.value === value)?.is_year;
            const selectedOption = options.find((o) => o.value === value);
            return isYear
                ? ""
                : translate.t(selectedOption?.label || '') + " " + selectedOption?.parent;
        });

        const filtered = s.filter((value) => value !== "");
        return filtered.join(", ");
    }

    const allSelected = selectedValues.length === options.length;

    return (
        <FormControl fullWidth>
            <InputLabel>Mês/Ano</InputLabel>
            <Select
                label="Mês/Ano"
                multiple
                value={selectedValues}
                onChange={handleChange}
                renderValue={renderValues}
                MenuProps={{
                    PaperProps: {
                        style: {
                            maxHeight: '400px',
                        },
                    },
                    disableAutoFocusItem: true,
                }}
            >
                {options.map((option) => (
                    <MenuItem key={option.value}
                              value={option.value}
                              style={{paddingLeft: option.parent ? 32 : 16, paddingTop: 0, paddingBottom: 0}}
                    >
                        <Checkbox checked={selectedValues.includes(option.value)}/>
                        <ListItemText primary={option.parent ? translate.t(option.label) : option.label}/>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export default MultiLevelSelect;
